import React, { useCallback, useEffect, useState } from "react";
import {
  List,
  Typography,
  Avatar,
  Space,
  Flex,
  Button,
  Dropdown,
  Badge,
  Modal,
} from "antd";
import {
  EditOutlined,
  KeyOutlined,
  LogoutOutlined,
  RightOutlined,
  UserAddOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { updateCurrentChat, addNewMessage } from "../../../redux/slices/chatSlice";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import { useChatSidebarContext } from "../../../context/ChatSidebarScreenContext";
import { formatChats, formatMessage } from "../../../utils";
import { useSocket } from "../../../context/SocketContext";
import ChatSidebarHeader from "../../../components/common/ChatSidebarHeader";
import GroupPermissions from "./GroupPermissions";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import CustomLoader from "../../../components/common/UI/Loader";
import { storageService } from "../../../services/storageService";
import UpdateGroup from "./UpdateGroup";
import AddNewMember from "./AddNewMember";
import "./index.scss";

const { Title, Text } = Typography;

const GroupInfo = () => {
  const dispatch = useAppDispatch();
  const { currentChat } = useSelector((state: RootState) => state.chat);
  const { socket } = useSocket();
  const { setCurrentScreen } = useChatSidebarContext();
  const { contacts } = useSelector((state: RootState) => state.contacts);
  const [currentGroupScreen, setCurrentGroupScreen] = useState("info");
  const [groupInfo, setGroupInfo] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exitModalOpen, setExitModalOpen] = useState<boolean>(false);
  const [userNotFound, setUserNotFound] = useState<boolean>(false);
  const [showGroupSetting, setShowGroupSetting] = useState<boolean>(false);
  const [showAddMembers, setShowAddMembers] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [leavingGroup, setLeavingGroup] = useState<boolean>(false);
  const [groupMembers, setGroupMembers] = useState<any>(null);
  const userId = storageService.getUserId();

  const handleBackToInfo = () => {
    setCurrentGroupScreen("info");
  };

  const addContactNames = (conversation: any) => {
    const membersWithSavedNames = conversation[0].groupMembers.map(
      (member: any) => {
        const matchedContact = contacts.find(
          (contact: any) => contact.phoneNumber === member.phoneNumber
        );

        if (matchedContact) {
          return { ...member, savedName: matchedContact.fullName };
        } else {
          return { ...member, savedName: "" };
        }
      }
    );

    const updatedListWithSavedNames = {
      ...conversation[0],
      groupMembers: membersWithSavedNames,
    };

    return updatedListWithSavedNames;
  };

  const fetchGroupInfo = useCallback(async () => {
    const URL = `/chat/new-conversation-list?cId=${currentChat?.id}`;

    setLoading(true);
    try {
      const response = await apiInstance.get(URL);
      if (response.status === 200) {
        // debugger
        if (currentChat?.isArchived) {
          const conversation = formatChats(
            response.data.newCnv?.docs?.[0]?.archivedList
          );

          setGroupInfo(addContactNames(conversation));
        } else {
          const conversation = formatChats(
            response.data.newCnv?.docs?.[0]?.conversationList
          );

          setGroupInfo(addContactNames(conversation));
        }
      }
    } catch (err: any) {
      console.error("Error fetching conversations:", err);
      toast.error(err.message || "There was an error fetching Conversations");
    } finally {
      setLoading(false);
    }
  }, [currentChat]);

  const handleMakeAdmin = async (memberId: string) => {
    const URL = ENDPOINTS.GROUP_PERMISSION_UPDATE;

    setLoading(true);
    try {
      const response = await apiInstance.post(URL, {
        groupId: currentChat?.id,
        makeAdmin: memberId,
      });
      if (response.status === 200) {
        fetchGroupInfo();
      }
    } catch (err: any) {
      console.error("Error making admin:", err);
      toast.error(err.message || "There was an error. Please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (memberId: string, phoneNumber: string) => {
    const encodedPhone = encodeURIComponent(phoneNumber);
    const URL =
      ENDPOINTS.MEMBER_UPDATE +
      `?memberId=${encodedPhone}&chatId=${currentChat?.id}`;

    setLeavingGroup(true);
    try {
      const response = await apiInstance.delete(URL);

      if (response.status === 200) {
        await fetchGroupInfo();

        const newMsg = formatMessage(
          response?.data?.newMessage || response?.data?.conversation,
          userId
        );

        if (newMsg?.message) {
          dispatch(addNewMessage(newMsg));
        }

        const isRemovingSelf = memberId === userId;

        if (isRemovingSelf) {
          dispatch(
            updateCurrentChat({
              groupMembers: currentChat?.groupMembers?.filter(
                (member: any) => member._id !== userId
              ),
            })
          );
        } else {
          setGroupInfo((prev: any) => ({
            ...prev,
            groupMembers: prev.groupMembers.filter(
              (member: any) => member._id !== memberId
            ),
          }));
        }
      }
    } catch (err: any) {
      console.error("Error removing member:", err);
      toast.error(err.message || "There was an error. Please try again");
    } finally {
      setLeavingGroup(false);
      setExitModalOpen(false);
    }
  };

  useEffect(() => {
    fetchGroupInfo();
  }, [currentGroupScreen, fetchGroupInfo]);

  useEffect(() => {
    if (groupInfo) {
      const members = groupInfo.groupMembers;
      const currentUser = members.find((member: any) => member?._id === userId);

      const reorderedMembers = [...members];
      const currentUserIndex = reorderedMembers?.findIndex(
        (item: any) => item?._id === currentUser?._id
      );

      if (currentUserIndex > -1) {
        const [currentUserItem] = reorderedMembers.splice(currentUserIndex, 1);
        reorderedMembers.unshift(currentUserItem);
      }

      setGroupMembers(reorderedMembers);

      if (currentUser) {
        if (currentUser?.isAdmin) {
          setIsAdmin(true);
          setShowGroupSetting(true);
          setShowAddMembers(true);
        } else {
          const groupSetting = groupInfo?.groupSettings;
          // debugger;
          if (groupSetting?.editGroup) {
            setShowGroupSetting(true);
          } else {
            setShowGroupSetting(false);
          }
          if (groupSetting?.addOthersMembers) {
            setShowAddMembers(true);
          } else {
            setShowAddMembers(false);
          }
        }
      } else {
        setUserNotFound(true);
      }
    }
  }, [groupInfo, userId]);

  useEffect(() => {
    if (!socket) return;

    const handler = () => fetchGroupInfo();

    socket.on("user-updates", handler);

    return () => {
      socket.off("user-updates", handler);
    };
  }, [fetchGroupInfo, socket]);

  return (
    <>
      {currentGroupScreen === "info" && (
        <div className="group-info-wrapper">
          <ChatSidebarHeader
            title="List Info"
            disabled={loading}
            handleBackClick={() => {
              setCurrentScreen(currentChat?.previousScreen || "chats");
            }}
          />

          <div className="main">
            {loading ? (
              <Flex className="w-full" justify="center">
                <CustomLoader />
              </Flex>
            ) : (
              <>
                <div
                  className={`group-info-card ${userNotFound && "disabled"}`}
                >
                  <Flex
                    className="w-full"
                    align="center"
                    justify="space-between"
                  >
                    <Flex align="center" gap={8}>
                      <Avatar
                        src={groupInfo?.image}
                        className="avatar"
                        size={45}
                      >
                        {groupInfo?.name?.[0]?.toUpperCase()}
                      </Avatar>
                      <Space direction="vertical" size={0}>
                        <Title className="card-title">{groupInfo?.name}</Title>
                        <Text className="group-date">
                          Created on {groupInfo?.date}
                        </Text>
                      </Space>
                    </Flex>

                    {showGroupSetting && (
                      <EditOutlined
                        className="icon-edit cursor-pointer"
                        onClick={() => {
                          if (userNotFound) return;
                          setCurrentGroupScreen("update-group-info");
                        }}
                      />
                    )}
                  </Flex>
                </div>

                {isAdmin && (
                  <div
                    className={`group-info-card ${userNotFound && "disabled"}`}
                    onClick={() => {
                      if (userNotFound) return;
                      setCurrentGroupScreen("permissions");
                    }}
                  >
                    <Flex
                      className="w-full h-full cursor-pointer"
                      align="center"
                      justify="space-between"
                    >
                      <Flex align="center" gap={8}>
                        <div className="perimission-icon">
                          <KeyOutlined className="icon-key" />
                        </div>
                        <Title className="card-title">Group Permission</Title>
                      </Flex>
                      <RightOutlined className="right-icon" />
                    </Flex>
                  </div>
                )}

                <Text className="recipients">
                  RECIPIENTS : {groupInfo?.groupMembers?.length}
                </Text>

                <div className="group-section">
                  {showAddMembers && (
                    <Flex
                      className={`w-full cursor-pointer ${
                        userNotFound && "disabled"
                      }`}
                      align="center"
                      justify="space-between"
                      onClick={() => {
                        if (userNotFound) return;
                        setCurrentGroupScreen("add-new-member");
                      }}
                    >
                      <Flex align="center" gap={8}>
                        <Avatar className="avatar" size={45}>
                          <UserAddOutlined />
                        </Avatar>
                        <Title className="card-title">Add Members</Title>
                      </Flex>
                      <RightOutlined className="right-icon" />
                    </Flex>
                  )}

                  <List
                    className="members-list"
                    dataSource={groupMembers || []}
                    renderItem={(member: any) => (
                      <Flex
                        className="w-full"
                        align="center"
                        justify="space-between"
                      >
                        <Flex align="center" gap={8}>
                          <Avatar className="avatar" size={45}>
                            <UserAddOutlined />
                          </Avatar>
                          <Flex vertical className="card-title">
                            <Space>
                              {member?.savedName
                                ? member?.savedName
                                : member?.phoneNumber}
                              {member?.isAdmin && (
                                <Badge className="custom-badge">Admin</Badge>
                              )}
                            </Space>
                            {member.savedName && (
                              <Text className="card-sub-title">
                                {member?.phoneNumber}
                              </Text>
                            )}
                          </Flex>
                        </Flex>
                        {!userNotFound && member?._id !== userId && isAdmin && (
                          <Dropdown
                            overlayClassName="custom-dropdown"
                            menu={{
                              items: [
                                {
                                  key: "1",
                                  label: (
                                    <span
                                      onClick={() => {
                                        handleMakeAdmin(member?._id);
                                      }}
                                    >
                                      Make Admin
                                    </span>
                                  ),
                                },
                                {
                                  key: "2",
                                  label: (
                                    <span
                                      onClick={() =>
                                        handleRemove(
                                          member._id,
                                          member.phoneNumber
                                        )
                                      }
                                    >
                                      Remove
                                    </span>
                                  ),
                                },
                              ],
                            }}
                          >
                            <Button
                              type="text"
                              className="more-btn"
                              icon={<MoreOutlined />}
                            />
                          </Dropdown>
                        )}
                      </Flex>
                    )}
                  />
                </div>

                {groupInfo?.pastMembers?.length > 0 && (
                  <>
                    <Text className="recipients">
                      PAST MEMBERS : {groupInfo?.pastMembers?.length}
                    </Text>

                    <div className="group-section">
                      <List
                        className="past-members-list"
                        dataSource={groupInfo?.pastMembers}
                        renderItem={(member: any) => (
                          <Flex
                            className="w-full"
                            align="center"
                            justify="space-between"
                          >
                            <Flex align="center" gap={8}>
                              <Avatar
                                src={member.profileImage}
                                className="avatar"
                                size={45}
                              >
                                <UserAddOutlined />
                              </Avatar>
                              <Title className="card-title">
                                {member?.phoneNumber}
                              </Title>
                            </Flex>
                          </Flex>
                        )}
                      />
                    </div>
                  </>
                )}

                <div
                  onClick={() => setExitModalOpen(true)}
                  className={`group-info-card cursor-pointer ${
                    userNotFound && "disabled"
                  }`}
                >
                  <Flex className="h-full" align="center" gap={8}>
                    <LogoutOutlined className="exit-icon" />
                    <Title className="card-title">Exit group</Title>
                  </Flex>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {currentGroupScreen === "permissions" && (
        <GroupPermissions
          currentChat={groupInfo}
          handleBackToInfo={handleBackToInfo}
        />
      )}

      {currentGroupScreen === "update-group-info" && (
        <UpdateGroup groupInfo={groupInfo} handleBackClick={handleBackToInfo} />
      )}

      {currentGroupScreen === "add-new-member" && (
        <AddNewMember
          groupInfo={groupInfo}
          handleBackClick={handleBackToInfo}
        />
      )}

      <Modal
        className="custom-modal"
        centered
        title="Confirm Exit"
        open={exitModalOpen}
        onCancel={() => setExitModalOpen(false)}
        okButtonProps={{
          loading: leavingGroup,
          className: "modal-ok-btn",
        }}
        cancelButtonProps={{
          className: "modal-cancel-btn",
        }}
        onOk={() => {
          const user = groupInfo.groupMembers.find(
            (member: any) => member?._id === userId
          );
          const userPhoneNumber = user?.phoneNumber;
          handleRemove(user._id, userPhoneNumber);
        }}
      >
        <Text className="text-primary">
          Are you sure you want to leave this group?
        </Text>
      </Modal>
    </>
  );
};

export default GroupInfo;
